import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import {
  activeTabAtom,
  sidebarCollapsedAtom,
  chatSidebarCollapsedAtom,
  chatSidebarWidthAtom,
  globalKeymapModalOpenAtom
} from '../../../../atoms/v3/uiAtoms';
import { DesktopSidebar } from './DesktopSidebar';
import { DesktopChatSidebar } from './DesktopChatSidebar';
import { trackPageView } from '../../../../lib/analytics';

import { KeymapModal } from '../tasks/KeymapModal';
import { useIOSViewportFix } from '../../../../hooks/v3/useIOSViewportFix';
import { UndoToastContainer } from '../../../../components/v3/UndoToastContainer';
import { updateTaskAtom } from '../../../../atoms/v0/taskAtoms';
import { showPersonalizationAtom, showCharacterMarketAtom } from '../../../../atoms/v3/uiAtoms';
import { ChatPersonalizationSettings } from '../../ChatPersonalizationSettings';
import { CharacterMarket } from '../../CharacterMarket';

/**
 * 桌面三栏布局组件
 *
 * 实现了一个符合 iOS/iPadOS/macOS 设计规范的三栏式布局：
 * - 左侧栏：导航和大纲区域，可折叠
 * - 中间栏：主内容区域
 * - 右侧栏：可选的详情区域（通过 Outlet 渲染）
 */
export const DesktopThreeColumnLayout: React.FC = () => {
  useIOSViewportFix();
  const location = useLocation();
  const [activeTabState, setActiveTab] = useAtom(activeTabAtom);
  const [sidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [chatSidebarCollapsed] = useAtom(chatSidebarCollapsedAtom);
  const [chatSidebarWidth] = useAtom(chatSidebarWidthAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [showPersonalization, setShowPersonalization] = useAtom(showPersonalizationAtom);
  const [showCharacterMarket, setShowCharacterMarket] = useAtom(showCharacterMarketAtom);

  // 快捷键帮助弹窗状态（现在由 useShortcutManager 统一管理）
  const [isKeymapModalOpen, setIsKeymapModalOpen] = useAtom(globalKeymapModalOpenAtom);

  // 获取当前页面类型
  const getCurrentPageType = () => {
    const path = location.pathname;
    if (path.includes('/v3/taskv2')) return 'taskv2';
    if (path.includes('/v3/chat')) return 'chat';
    if (path.includes('/v3/me')) return 'me';
    return 'unknown';
  };
  const currentPageType = getCurrentPageType();

  // 处理撤销操作
  const handleUndo = (taskId: string, previousState: boolean) => {
    updateTask({
      id: taskId,
      done: previousState
    });
  };

  // 处理路径变化，更新活动标签和历史记录
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const basePath = pathParts[2] || 'tasks'; // 默认为 tasks

    let newDerivedTab: string | null = null;
    let pageNameForAnalytics = basePath; // 分析用的页面名称默认为basePath

    // 1. 直接匹配的标签页
    if (['taskv2', 'projectv2', 'tasks', 'calendar', 'me', 'focus'].includes(basePath)) {
      newDerivedTab = basePath;
    }
    // 2. 项目详情页: 尝试保持父标签激活状态，否则默认为 'projectv2'
    else if (basePath === 'projects') {
      if (activeTabState && ['taskv2', 'projectv2', 'tasks', 'calendar', 'me', 'focus'].includes(activeTabState)) {
        newDerivedTab = activeTabState;
      } else {
        newDerivedTab = 'projectv2'; // 直接访问项目页或父标签无效时的默认值
      }
      pageNameForAnalytics = 'project_detail'; // 更具体的页面名称
    }
    // 3. 桌面端不直接激活 'chat' 标签，默认为 'taskv2'
    else if (['chat', 'chat-list', 'chat-detail'].includes(basePath)) {
      newDerivedTab = 'taskv2';
      // pageNameForAnalytics 保持 basePath (e.g., 'chat-detail')
    }
    // 4. 其他未知路径: 尝试保持当前有效标签，否则默认为 'taskv2'
    else {
      if (activeTabState && ['taskv2', 'projectv2', 'tasks', 'calendar', 'me', 'focus'].includes(activeTabState)) {
        newDerivedTab = activeTabState;
      } else {
        newDerivedTab = 'taskv2'; // 未知路径的后备默认值
      }
    }

    // 只有当推断出的新标签与当前状态不同时才更新，避免不必要的重渲染和循环
    if (newDerivedTab && newDerivedTab !== activeTabState) {
      console.log(`[DesktopThreeColumnLayout] Path: ${location.pathname}, DerivedTab: ${newDerivedTab}, CurrentActive: ${activeTabState} -> Setting active tab.`);
      setActiveTab(newDerivedTab);
    }

    // 记录页面访问事件
    // Defer this call to prevent blocking the main thread during initial rendering and state updates.
    setTimeout(() => {
      trackPageView(pageNameForAnalytics, {
        path: location.pathname,
        tab: newDerivedTab || activeTabState || 'unknown', // 使用推断的标签，或当前激活标签，或未知
      });
    }, 0);
  }, [location.pathname, activeTabState, setActiveTab]);

  return (
    <div className="flex h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 font-ios">
      {/* 左侧边栏 */}
      <DesktopSidebar />

      {/* 主内容区域 */}
      <main
        className={`flex-1 overflow-auto transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? 'ml-[4.375rem]' : 'ml-[16.25rem]'
        }`}
        style={{
          marginRight: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`
        }}
      >
        <Outlet />
      </main>

      {/* 右侧对话栏 */}
      <DesktopChatSidebar />

      {/* 撤销 Toast 容器 */}
      <UndoToastContainer onUndo={handleUndo} />

      {/* 个性化设置模态框 */}
      {showPersonalization && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg shadow-ios dark:shadow-ios-dark max-w-2xl w-full h-[90vh] mx-4 flex flex-col">
            <ChatPersonalizationSettings isEmbedded={true} onClose={() => setShowPersonalization(false)} />
          </div>
        </div>
      )}

      {/* 角色卡市场模态框 */}
      {showCharacterMarket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg shadow-ios dark:shadow-ios-dark max-w-4xl w-full h-[90vh] mx-4 flex flex-col">
            <CharacterMarket isEmbedded={true} onClose={() => setShowCharacterMarket(false)} />
          </div>
        </div>
      )}

      {/* 全局键盘快捷键说明弹窗 */}
      <KeymapModal
        isOpen={isKeymapModalOpen}
        onClose={() => setIsKeymapModalOpen(false)}
        currentPageType={currentPageType}
      />
    </div>
  );
};
